/* eslint-disable max-lines -- the parent now ~22 effective lines over
 * the 400 ceiling. What's left is irreducible orchestration: 22 hook
 * calls (each owning one cross-cutting concern: thread state, channel
 * messages, pins, workspace mentions data, scroll request, thread
 * routing, drag-drop, terminal events, mark-read, send-to-shell, ...)
 * plus the two render leaves (`MessageAreaContent` lives inline as the
 * inner drop-target div + 7 children with ~30 props each;
 * `MessageAreaPanels` owns the right-side panels). Splitting further
 * either inflates prop interfaces past readability (the JSX trees fan
 * out to 25–30 props each) or breaks the multiplex contract that
 * keydown / interval / RO callbacks must be gated on `hidden` from
 * the same scope as the `useEffect` that registers them. The smaller
 * seams (composite-channel data, pins, workspace data, scroll
 * request, thread routing, render diagnostics, channel/user click
 * stabilization, suggest-insert, dm-invite) are all extracted into
 * `message-area/`. */
import { MESSAGE_PAGE_SIZE } from "@/lib/message-limits";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useQueryDebugLog } from "@/hooks/use-debug-query";
import { queries } from "@/lib/queries";
import { mutators } from "@/lib/mutators";
import {
  useRenderUserMap,
  useActiveUserMap,
  useLastReadMap,
} from "@/lib/selectors";
import { useCurrentUser } from "@/lib/current-user";
import { MessageInput } from "@/components/messages/MessageInput";
import { ChatCircle } from "@/components/ui/icons";
import {
  EmptyState,
  MissingRouteAdmonition,
} from "@/components/ui/empty-state";
import { useCall } from "@/components/calls/CallProvider";
import { TypingIndicator } from "./TypingIndicator";
import { MessageListView, type MessageListViewHandle } from "./MessageListView";
import { StreamingDmBubble } from "@/components/messages/StreamingDmBubble";
import { csNow, csLog } from "@/lib/channel-switch-profile";

import { useMessageAreaDragDrop } from "./message-area/use-drag-drop";
import { useTerminalEventListeners } from "./message-area/use-terminal-event-listeners";
import { DropOverlay } from "./message-area/drop-overlay";
import { MessageAreaHeader } from "./message-area/message-area-header";
import { useActiveCall } from "./message-area/use-active-call";
import { useMessageLimit } from "./message-area/use-message-limit";
import { useMarkChannelRead } from "./message-area/use-mark-channel-read";
import { useDmDerivations } from "./message-area/use-dm-derivations";
import { useSendToShell } from "./message-area/use-send-to-shell";
import { useRenderDiagnostics } from "./message-area/use-render-diagnostics";
import { useThreadState } from "./message-area/use-thread-state";
import { useChannelMessageList } from "./message-area/use-channel-message-list";
import { useChannelPins } from "./message-area/use-channel-pins";
import { useWorkspaceMentionsData } from "./message-area/use-workspace-mentions-data";
import { useScrollRequest } from "./message-area/use-scroll-request";
import { useThreadRouting } from "./message-area/use-thread-routing";
import { usePendingCallAutoJoin } from "./message-area/use-pending-call-auto-join";
import { useDmInviteHandler } from "./message-area/use-dm-invite-handler";
import { useSuggestInsert } from "./message-area/use-suggest-insert";
import { useChannelUserHandlers } from "./message-area/use-channel-user-handlers";
import { MessageAreaPanels } from "./message-area/MessageAreaPanels";
import type { MessageAreaProps } from "./message-area/message-area-types";

export const MessageArea = memo(function MessageArea({
  channelId,
  workspaceId,
  onSelectChannel,
  onToggleTerminal,
  onOpenMobileSidebar,
  targetMessageId,
  targetThreadId,
  onTargetMessageHandled,
  isDmChannelHint = false,
  pendingCallJoin,
  onPendingCallJoinHandled,
  aboveComposer,
  hidden = false,
}: MessageAreaProps) {
  const listRef = useRef<MessageListViewHandle>(null);

  // Channel-switch profile — synchronous body cost vs commit cost (incl.
  // descendant subtree). High body, low commit gap → cost is in this
  // file's 22 hooks. Low body, high commit gap → cost is in descendant
  // components (MessageListView, MessageInput, MessageRow tree).
  const csRenderStart = csNow();
  const csRenderStartRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  csRenderStartRef.current = csRenderStart;

  useLayoutEffect(() => {
    csLog(
      "MessageArea.commit",
      csRenderStartRef.current,
      `ch=${channelId?.slice(-6) ?? "-"}`,
    );
  });

  // ─── Render diagnostics ─────────────────────────────────────────
  useRenderDiagnostics(channelId, hidden, listRef);

  const { user } = useCurrentUser();
  const zero = useZero();

  const { setThreadState, activeThread, setActiveThread } =
    useThreadState(channelId);

  // mig 130 — inline-reply target state. Lives at the MessageArea level
  // because MessageInput is multiplexed across channels and would lose
  // state on switch. Each MessageArea instance has its own pending reply,
  // so a user can have different drafts pending per channel.
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(
    null,
  );
  const handleSetReplyTarget = useCallback((messageId: string | null) => {
    setReplyingToMessageId(messageId);
  }, []);
  // useCallback to keep the prop reference stable across MessageArea
  // renders — without this, the inline `() => handleSetReplyTarget(null)`
  // would be a fresh function each render, busting MessageInput's
  // React.memo on every parent re-render. Same cascade pattern PRs
  // #1326 / #1371 fixed via useCallback / useStable* helpers.
  const handleClearReply = useCallback(
    () => handleSetReplyTarget(null),
    [handleSetReplyTarget],
  );
  // Discord-shape "Create Thread" — fires the action mutator (which
  // posts a system message announcing the thread) and opens the
  // sidebar at the source. The action's optimistic insert lands the
  // system message in the channel before the server round-trip.
  //
  // Double-click guard: the mutator has its own server-side
  // idempotent retry on `clientSystemMessageId` (PK match → no-op),
  // but each click here generates a FRESH UUID, so a second rapid
  // click would post a second distinct system message. The ref
  // tracks per-source in-flight clicks and ignores repeats within
  // ~1s. `messageId` is the source — if the user opens a different
  // message's thread immediately after, that's allowed.
  const createThreadInFlightRef = useRef<Set<string>>(new Set());
  const handleCreateThread = useCallback(
    (messageId: string) => {
      if (createThreadInFlightRef.current.has(messageId)) return;
      createThreadInFlightRef.current.add(messageId);
      const clientSystemMessageId = crypto.randomUUID();
      zero.mutate(
        mutators.actions.createThread({
          messageId,
          clientSystemMessageId,
        }),
      );
      setActiveThread(messageId);
      window.setTimeout(() => {
        createThreadInFlightRef.current.delete(messageId);
      }, 1000);
    },
    [zero, setActiveThread],
  );

  const { handleChannelClick, handleUserClick } =
    useChannelUserHandlers(onSelectChannel);

  // Single per-channel composite — replaces the prior 3-way fan-out
  // (channels.byId + channel_members.byChannel + pins.byChannel). With
  // multiplex cap = 5 mounted MessageAreas, this cuts per-user query
  // hubs by up to 10. Slice deps are destructured so an update to one
  // relation (e.g. pin added) doesn't churn consumers of another
  // (e.g. members list). Mirrors useTeamMemberAndAgentData:36-58.
  const compositeResult = useQuery(
    channelId ? queries.channels.byIdComposite(channelId) : null,
  );
  useQueryDebugLog("channels.byIdComposite", compositeResult);
  const [composite] = compositeResult;
  const channel = composite ?? null;
  const compositeMembers = composite?.members;
  const compositePins = composite?.pins;

  // Calls live at the App root now (CallProvider in App.tsx renders
  // <AppCallDialog/>). The local handleJoinCall closure preserves the
  // existing call-button signature `(mode, channelNameForCall?, opts?)`
  // so MessageAreaHeader / MessageListView / MessageRow stay unchanged.
  const { callsEnabled, joinCall, isCallActionDisabled } = useCall();
  const handleJoinCall = useCallback(
    async (
      mode: "audio" | "video",
      channelNameForCall?: string,
      opts?: { skipNotification?: boolean },
    ) => {
      // For DMs in Home view we don't have an active workspace —
      // null is fine, the server skips the workspace-id check and
      // relies on channel membership instead. The early return only
      // bails if there's literally no channel selected.
      if (!channelId) return;
      const targetWorkspaceId = workspaceId ?? channel?.workspace_id ?? null;
      await joinCall({
        channelId,
        workspaceId: targetWorkspaceId,
        channelName: channelNameForCall?.trim() || channel?.name || "channel",
        mode,
        skipNotification: opts?.skipNotification,
      });
    },
    [channelId, workspaceId, channel?.workspace_id, channel?.name, joinCall],
  );

  // Auto-join call when navigated from incoming call accept
  usePendingCallAutoJoin({
    pendingCallJoin,
    channelId,
    handleJoinCall,
    onPendingCallJoinHandled,
  });

  const [showMembers, setShowMembers] = useState(false);
  const [dmInviteContext, setDmInviteContext] = useState<
    import("@/components/dialogs/InviteDialog").InviteContext | null
  >(null);
  const { suggestInsert, setSuggestInsert, suggestInsertVersionRef } =
    useSuggestInsert(channelId);

  const filesDroppedRef = useRef<((files: File[]) => void) | null>(null);

  const {
    messageLimit,
    bumpInFlightRef: bumpLimitInFlightRef,
    bumpLimit,
    handleReturnToBottom,
    syncSliceLength: syncMessageLimitSliceLength,
  } = useMessageLimit({ channelId });

  const {
    channelMessages,
    messageList,
    messageById,
    threadSourceIds,
    latestMessageCreatedAt,
    isUsingReconnectSnapshot,
    showMessageLoadingSkeleton,
    channelMessagesStatusForList,
    hasMoreMessages,
  } = useChannelMessageList({
    channelId,
    messageLimit,
    channelCreatedAt: channel?.created_at ?? null,
    isDmChannel:
      isDmChannelHint || channel?.type === "dm" || channel?.type === "group_dm",
  });

  // mig 130 — derive the in-progress reply target's MessageData from
  // messageById. If the source isn't in the local map (rare — paginated
  // out or just-deleted), fall back to null and the pill won't render.
  const replyingToMessage = useMemo(() => {
    if (!replyingToMessageId) return null;
    return messageById.get(replyingToMessageId) ?? null;
  }, [replyingToMessageId, messageById]);

  // ─── Selector hooks (stable across typing heartbeats) ──────────────
  const [renderUserMap] = useRenderUserMap();
  const [activeUserMap] = useActiveUserMap(); // for MemberListPanel (needs is_online)
  const [lastReadMap] = useLastReadMap();
  // All user lookups now use renderUserMap from selector (stable across typing).

  const { pinnedMessageIds, visibleChannelPins, handlePin } = useChannelPins({
    channelId,
    user: user ?? null,
    messageById,
    zero,
    channelPinsRaw: compositePins,
  });

  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  // Ano Sync — Control Tower panel (default closed; one right panel at a time).
  const [showControlTower, setShowControlTower] = useState(false);

  // Resolve DM display name from channel members. Sourced from the
  // per-channel composite above (composite?.members slice). Spread to
  // a mutable array so consumer signatures don't shift; Zero IVM
  // preserves nested array identity for unchanged children.
  const channelMemberList = useMemo(
    () => (compositeMembers ? [...compositeMembers] : []),
    [compositeMembers],
  );
  const currentUserChannelMembership = useMemo(
    () => channelMemberList.find((m) => m.user_id === user?.id) ?? null,
    [channelMemberList, user?.id],
  );
  const isChannelMuted = Boolean(currentUserChannelMembership?.muted);
  const handleToggleChannelMute = useCallback(() => {
    if (!currentUserChannelMembership) return;
    zero.mutate(
      mutators.actions.toggleChannelMute({
        membershipId: currentUserChannelMembership.id,
        muted: !isChannelMuted,
      }),
    );
  }, [currentUserChannelMembership, isChannelMuted, zero]);

  const {
    isDM,
    dmDisplayName,
    dmHeaderAvatarUrl,
    dmHeaderOtherUserId,
    isCoworkerDm,
    coworkerDmUserId,
    coworkerIds,
  } = useDmDerivations({
    channel,
    user,
    channelMemberList,
    renderUserMap,
  });

  const {
    workspaceMembers,
    workspaceChannels,
    hasCollaborators,
    activeWorkspaceMemberIds,
  } = useWorkspaceMentionsData({
    workspaceId,
    channelId,
    renderUserMap,
    isDM,
    channelMemberList,
  });

  // ─── Scroll infrastructure (parent-owned) ──────────────────────────
  // scrollAnchorByChannel: per-channel saved scroll position with neighborId
  const scrollAnchorByChannel = useRef<
    Record<string, { id: string; neighborId: string | null; offset: number }>
  >({});
  const currentAnchorIdRef = useRef<string | null>(null);

  // ─── Limit-bump in-flight sync ─────────────────────────────────
  // Clear the in-flight flag the moment Zero delivers a larger slice.
  // The hook itself owns bumpLimit / handleReturnToBottom (see early
  // useMessageLimit call) — this is the render-time sync against the
  // live channelMessages length.
  syncMessageLimitSliceLength(channelMessages.length);

  // ─── MessageListView scroll request plumbing ───────────────────────
  const {
    scrollRequest,
    setScrollRequestForNotification,
    handleScrollToMessage,
    handleScrollRequestHandled,
  } = useScrollRequest({ onTargetMessageHandled });

  // Notification deep-link routing → thread panel or channel-level scroll.
  const { threadScrollTargetId, handleThreadScrollHandled } = useThreadRouting({
    channelId,
    targetMessageId,
    targetThreadId,
    onTargetMessageHandled,
    setThreadState,
    setScrollRequestForNotification,
    zero,
  });

  const handleSend = useCallback(() => {
    listRef.current?.scrollToBottom();
  }, []);

  // ─── Unread tracking: update last_read_at ─────────────────────────
  // Gate on `hidden` — this MessageArea instance is in the multiplex
  // kept-alive set but the user isn't looking at it. Without the guard,
  // a new message arriving on a background channel would silently mark
  // it as read (last_read_at = Date.now() + markChannelNotificationsRead),
  // clearing the user's unread badge for a channel they never returned to.
  useMarkChannelRead({
    channelId,
    hidden,
    user: user ?? null,
    latestMessageCreatedAt,
    lastReadMap,
    zero,
  });

  // Drag-and-drop handlers for file + terminal-card attachments.
  // Logic lives in `./message-area/use-drag-drop` so MessageArea doesn't
  // own ~80 lines of overlay-state + drop-routing.
  const {
    isDraggingFiles,
    isDraggingCard,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useMessageAreaDragDrop({
    user: user ?? null,
    channelId,
    zero,
    filesDroppedRef,
  });

  // 6 window event listeners for shell-process events (terminal:send-to-chat,
  // reply-to-thread, send-to-composer, snapshot, share-command, run-result).
  // Each fires a typed message insert / addReply. All gated on `hidden` so
  // only the active multiplexed MessageArea responds.
  useTerminalEventListeners({
    user: user ?? null,
    channelId,
    zero,
    hidden,
    setSuggestInsert,
    suggestInsertVersionRef,
  });

  // Stable identity for handleSendToShell — see hook for the rationale
  // (MessageRow memo + ref-pattern over messageById / renderUserMap).
  const handleSendToShell = useSendToShell({
    channelId,
    channelName: channel?.name,
    activeThreadId: activeThread,
    messageById,
    renderUserMap,
    zero,
  });

  const displayName = isDM ? dmDisplayName : channel?.name;
  const [profileUserId, setProfileUserId] = useState<string | null>(null);

  const handleJoinCallFromList = useCallback(
    (mode: "audio" | "video") => {
      void handleJoinCall(mode, displayName || "channel", {
        skipNotification: true,
      });
    },
    [handleJoinCall, displayName],
  );

  const activeCallMessageId = useActiveCall(messageList);

  const handleInviteToConversation = useDmInviteHandler({
    channel,
    channelId,
    user,
    channelMemberList,
    renderUserMap,
    setDmInviteContext,
  });

  // ─── Render ────────────────────────────────────────────────────────

  csLog("MessageArea.body", csRenderStart, `ch=${channelId?.slice(-6) ?? "-"}`);

  if (!channelId) {
    return (
      <div className="flex min-h-0 flex-1 bg-background">
        <EmptyState
          layout="pane"
          icon={ChatCircle}
          title="Welcome to Ano"
          description="Select a channel to start messaging."
        />
      </div>
    );
  }

  // Spaces (channels.type='space', post mig 120) are non-message
  // containers. If a user navigates to a space's id via deeplink,
  // notification routing, command-K paste, or stale history, render
  // an empty-state instead of the channel composer/header. Without
  // this guard the composer would render but every send attempt
  // would fail with "Spaces cannot receive messages" via the
  // mutator-side rejection. Round-5 self-review.
  if (channel?.type === "space") {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <MissingRouteAdmonition
          title={channel.name || "Space"}
          description="Spaces are containers for channels — pick a channel from the sidebar to start messaging."
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-w-0">
      <div
        className="flex-1 flex flex-col min-w-0 bg-background relative isolate overflow-hidden"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Titlebar drag region */}
        <div className="h-[28px] shrink-0 z-20" data-tauri-drag-region />
        <MessageAreaHeader
          channel={channel}
          channelMemberListCount={channelMemberList.length}
          displayName={displayName}
          isDM={isDM}
          isCoworkerDm={isCoworkerDm}
          hasCollaborators={hasCollaborators}
          dmHeaderOtherUserId={dmHeaderOtherUserId}
          dmHeaderAvatarUrl={dmHeaderAvatarUrl}
          workspaceId={workspaceId ?? channel?.workspace_id ?? null}
          callsEnabled={callsEnabled}
          isCallActionDisabled={isCallActionDisabled}
          visibleChannelPinsCount={visibleChannelPins.length}
          showPinnedMessages={showPinnedMessages}
          showControlTower={showControlTower}
          canToggleMute={Boolean(currentUserChannelMembership)}
          isMuted={isChannelMuted}
          onSelectProfile={setProfileUserId}
          onJoinCall={handleJoinCall}
          onToggleTerminal={onToggleTerminal}
          onOpenMobileSidebar={onOpenMobileSidebar}
          onToggleMute={handleToggleChannelMute}
          onTogglePinned={() => setShowPinnedMessages((v) => !v)}
          onToggleControlTower={() => {
            setShowControlTower((v) => {
              const next = !v;
              // Keep one right panel open at a time on narrow widths.
              if (next) {
                setShowMembers(false);
                setShowPinnedMessages(false);
              }
              return next;
            });
          }}
          onToggleMembers={() => setShowMembers((v) => !v)}
          onInviteToConversation={handleInviteToConversation}
        />

        <DropOverlay
          isDraggingFiles={isDraggingFiles}
          isDraggingCard={isDraggingCard}
        />

        {/* Chat messages */}
        <MessageListView
          ref={listRef}
          channelId={channelId}
          messageList={messageList}
          hasMoreMessages={hasMoreMessages}
          channelMessagesStatus={channelMessagesStatusForList}
          isUsingReconnectSnapshot={isUsingReconnectSnapshot}
          showMessageLoadingSkeleton={showMessageLoadingSkeleton}
          messageListLength={messageList.length}
          renderUserMap={renderUserMap}
          pinnedMessageIds={pinnedMessageIds}
          workspaceMembers={workspaceMembers}
          workspaceChannels={workspaceChannels}
          activeWorkspaceMemberIds={activeWorkspaceMemberIds}
          messageById={messageById}
          threadSourceIds={threadSourceIds}
          scrollAnchorByChannel={scrollAnchorByChannel}
          currentAnchorIdRef={currentAnchorIdRef}
          bumpInFlightRef={bumpLimitInFlightRef}
          onBumpLimit={bumpLimit}
          onThreadClick={setActiveThread}
          onInlineReply={handleSetReplyTarget}
          onCreateThread={handleCreateThread}
          onScrollToMessage={handleScrollToMessage}
          onUserClick={setProfileUserId}
          onChannelClick={handleChannelClick}
          onPin={handlePin}
          onSendToShell={handleSendToShell}
          scrollRequest={scrollRequest}
          onScrollRequestHandled={handleScrollRequestHandled}
          activeCallMessageId={activeCallMessageId}
          onJoinCall={handleJoinCallFromList}
          onReturnToBottom={handleReturnToBottom}
          messageLimit={messageLimit}
          messagePageSize={MESSAGE_PAGE_SIZE}
          userId={user?.id}
          hidden={hidden}
        />

        {/* In-flight coworker streaming reply — synthetic bubble that
            renders ephemeral SSE state for both coworker DMs and
            channel @-mentions. Disappears when the SSE finishes and the
            real Zero-replicated message takes over. The bubble's
            internal `useAgentDmStream(channelId)` returns nil when
            there's no active stream, so rendering it unconditionally
            per channel is safe. */}
        {channelId && (
          <StreamingDmBubble
            channelId={channelId}
            renderUserMap={renderUserMap}
            latestMessageUserId={channelMessages[0]?.user_id ?? null}
            latestMessageCreatedAt={channelMessages[0]?.created_at ?? null}
            workspaceMembers={workspaceMembers}
            channels={workspaceChannels}
            onUserClick={setProfileUserId}
            onChannelClick={handleChannelClick}
          />
        )}

        {/* Typing indicator — self-contained, never re-renders parent */}
        <TypingIndicator channelId={channelId} hidden={hidden} />

        {aboveComposer}
        {/* Message input */}
        <MessageInput
          channelId={channelId}
          channelName={displayName}
          isDM={isDM}
          workspaceMembers={workspaceMembers}
          onFilesDroppedRef={filesDroppedRef}
          postingRestriction={channel?.posting_restriction}
          externalInsert={suggestInsert}
          onSend={handleSend}
          isCoworkerDm={isCoworkerDm}
          coworkerIds={coworkerIds}
          workspaceId={workspaceId ?? channel?.workspace_id ?? null}
          coworkerDmUserId={coworkerDmUserId}
          replyingToMessageId={replyingToMessageId}
          replyingToMessage={replyingToMessage}
          onClearReply={handleClearReply}
          hidden={hidden}
        />
      </div>

      <MessageAreaPanels
        channelId={channelId}
        showPinnedMessages={showPinnedMessages}
        visibleChannelPins={visibleChannelPins}
        messageById={messageById}
        renderUserMap={renderUserMap}
        onClosePinned={() => setShowPinnedMessages(false)}
        onScrollToMessage={handleScrollToMessage}
        showControlTower={showControlTower}
        onCloseControlTower={() => setShowControlTower(false)}
        showMembers={showMembers}
        onCloseMembers={() => setShowMembers(false)}
        channelMemberList={channelMemberList}
        activeUserMap={activeUserMap}
        activeThread={activeThread}
        workspaceMembers={workspaceMembers}
        workspaceChannels={workspaceChannels}
        activeWorkspaceMemberIds={activeWorkspaceMemberIds}
        onCloseThread={() => setActiveThread(null)}
        onUserClick={setProfileUserId}
        onChannelClick={handleChannelClick}
        onSendToShell={handleSendToShell}
        isCoworkerDm={isCoworkerDm}
        coworkerIds={coworkerIds}
        threadScrollTargetId={threadScrollTargetId}
        onThreadScrollHandled={handleThreadScrollHandled}
        hidden={hidden}
        dmInviteContext={dmInviteContext}
        onCloseDmInvite={() => setDmInviteContext(null)}
        profileUserId={profileUserId}
        onCloseProfile={() => setProfileUserId(null)}
        onStartDmFromProfile={handleUserClick}
      />
    </div>
  );
});
